import { useMemo, useRef, useState } from "react"
import { debounce } from "ts-debounce"
import backend from "~/backend"
import useDidUpdateEffect from "~/hooks/useDidUpdateEffect"

type Props = {
  dayId: number
  initialData: DietType
}

type DietType = {
  weight: string
  calories: string
}

export default function Diet({ dayId, initialData }: Props) {
  const [weight, setWeight] = useState(initialData.weight)
  const [calories, setCalories] = useState(initialData.calories)

  const saved = useRef(initialData)

  const save = (key, value) => {
    if (saved.current[key] === value) return

    saved.current[key] = value
    backend.updateDiet(dayId, { [key]: value })
  }

  const persistWeight = useMemo(
    () => debounce((weight) => save("weight", weight), 5000),
    [dayId]
  )

  const persistCalories = useMemo(
    () => debounce((calories) => save("calories", calories), 5000),
    [dayId]
  )

  useDidUpdateEffect(() => {
    persistWeight(weight).catch(() => {})
  }, [weight])

  useDidUpdateEffect(() => {
    persistCalories(calories).catch(() => {})
  }, [calories])

  const saveNow = (debounced, key, value) => {
    debounced.cancel()
    save(key, value)
  }

  const blurOnKey = (e) => {
    if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur()
  }

  return (
    <div className="flex flex-col gap-2 border-l-4 border-info pl-4 ">
      <a className="text-lg font-medium" href="/diet">
        Diet
      </a>

      <div>
        <div className="diet-row">
          <span className="diet-label">Weight</span>
          <div className="flex items-end">
            <input
              className="diet-input"
              type="text"
              inputMode="decimal"
              value={weight}
              onFocus={(e) => e.target.select()}
              onKeyDown={blurOnKey}
              onBlur={() => saveNow(persistWeight, "weight", weight)}
              onChange={(v) => setWeight(v.target.value.replace(",", "."))}
            />
            <div className="w-4 pb-1 text-xs text-fg-muted">kg</div>
          </div>
        </div>

        <div className="diet-row">
          <span className="diet-label">Calories</span>
          <div className="flex items-end">
            <input
              className="diet-input"
              type="text"
              inputMode="numeric"
              value={calories}
              onFocus={(e) => e.target.select()}
              onKeyDown={blurOnKey}
              onBlur={() => saveNow(persistCalories, "calories", calories)}
              onChange={(v) => setCalories(v.target.value)}
            />
            <div className="w-4 pb-1.5 text-xs text-fg-muted">ccal</div>
          </div>
        </div>
      </div>
    </div>
  )
}
